import React, { useState, useCallback } from 'react';
import TaskModal from '../modals/TaskBuiltInFormModal';
import ProjectModal from './modals/ProjectModal';
import { Box, Heading, Text, VStack, HStack, Badge, Divider, Button, Flex, SimpleGrid, Input, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure } from '@chakra-ui/react';
import { ViewIcon, EditIcon } from '@chakra-ui/icons';
import { Project, CreateProjectData } from '../../api/projectApi';
import { useAuthenticatedUser } from '../../contexts/AuthenticatedUserInfoContext';
import TaskDetails from '../../pages/TaskDetails';
import { setLastViewedProjectId } from '../../utils/localStorage';
import { useArchiveProject, useAddSpecFile, useRemoveSpecFile, useUpdateProject } from '../../hooks/useProjects';
import { useCreateTask, useUpdateTask, useTasks, useDeleteTask } from '../../hooks/useTasks';
import { useUsers } from '../../hooks/useUsers';
import { useClients } from '../../hooks/useClients';
import { useQueryClient } from '@tanstack/react-query';
import TaskTable from '../TaskTable';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrganizationEnums } from '../../hooks/useOrganizationEnums';
import { canModifyProject } from '../../utils/permissions';

interface ProjectOverviewProps {
  project: Project;
}

const LAST_VIEWED_TASK_KEY = 'lastViewedTaskId';

const ProjectOverview: React.FC<ProjectOverviewProps> = ({ project }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const addSpecFileMutation = useAddSpecFile();
  const removeSpecFileMutation = useRemoveSpecFile();
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskFormData, setTaskFormData] = useState<any>({
    taskTitle: '',
    priority: 'medium',
    description: '',
    type: 'development',
    status: 'Not Started',
    authBy: [],
    quotedPrice: undefined,
    agreedPrice: undefined,
    dateRequested: undefined,
    dateStarted: undefined,
    dueDate: undefined,
    assignedToUser: [],
  });
  
  const { authenticatedUser } = useAuthenticatedUser();
  const isProjectOwner = React.useMemo(() => {
    return authenticatedUser && project.userWhoCreatedProject && authenticatedUser.LoggedInUserId === project.userWhoCreatedProject._id;
  }, [authenticatedUser, project.userWhoCreatedProject]);
  // Check if user is assigned to this project
  const isUserAssigned = React.useMemo(() => {
    if (!authenticatedUser || !project.assignedToUser) return false;
    return Array.isArray(project.assignedToUser)
      ? project.assignedToUser.some((u: any) => {
          if (typeof u === 'string') return u === authenticatedUser.LoggedInUserId;
          if (u && typeof u === 'object' && (u._id || u.$oid)) {
            return u._id === authenticatedUser.LoggedInUserId || u.$oid === authenticatedUser.LoggedInUserId;
          }
          return false;
        })
      : false;
  }, [authenticatedUser, project.assignedToUser]);
  const canEditOrDeleteOrArchiveOrAddTask = canModifyProject(
    authenticatedUser,
    !!isProjectOwner,
    isUserAssigned
  );
  
  // State for project editing
  const [formData, setFormData] = useState<CreateProjectData>({
    userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
    projectName: project.projectName || '',
    type: project.type || 'software',
    client: typeof project.client === 'string' ? project.client : project.client?._id || '',
    description: project.description || '',
    authorizedBy: Array.isArray(project.authorizedBy) 
      ? project.authorizedBy
      : [],
    assignedToUser: Array.isArray(project.assignedToUser)
      ? project.assignedToUser.map((u: any) => typeof u === 'string' ? u : u._id)
      : [],
    quotedPrice: project.quotedPrice,
    agreedPrice: project.agreedPrice,
    toBeginOn: project.toBeginOn,
    toBeCompletedBy: project.toBeCompletedBy,
    spec: project.spec || [],
  });

  const { data: organizationEnums } = useOrganizationEnums();
  const projectTypes = organizationEnums?.projectTypes ?? [];
  
  // Update form data when project changes
  React.useEffect(() => {
    setFormData({
      userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
      projectName: project.projectName || '',
      type: project.type || 'software',
      client: typeof project.client === 'string' ? project.client : project.client?._id || '',
      description: project.description || '',
      authorizedBy: Array.isArray(project.authorizedBy) 
        ? project.authorizedBy
        : [],
      assignedToUser: Array.isArray(project.assignedToUser)
        ? project.assignedToUser.map((u: any) => typeof u === 'string' ? u : u._id)
        : [],
      quotedPrice: project.quotedPrice,
      agreedPrice: project.agreedPrice,
      toBeginOn: project.toBeginOn,
      toBeCompletedBy: project.toBeCompletedBy,
      spec: project.spec || [],
    });
  }, [project, authenticatedUser]);
  
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  // Modal state for file description
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingSpecIdx, setPendingSpecIdx] = useState<number | null>(null);
  const [pendingFileUrl, setPendingFileUrl] = useState<string | null>(null);
  const [pendingFileDesc, setPendingFileDesc] = useState<string>('');
  
  // Project editing state
  const { 
    isOpen: isProjectModalOpen, 
    onOpen: onProjectModalOpen, 
    onClose: onProjectModalClose 
  } = useDisclosure();
  
  const archiveProjectMutation = useArchiveProject();
  const { mutateAsync: createTask, isPending: isCreatingTask } = useCreateTask();
  const { mutateAsync: updateTask, isPending: isUpdatingTask } = useUpdateTask();
  const { mutateAsync: deleteTask } = useDeleteTask();
  const { mutateAsync: updateProject, isPending: isUpdatingProject } = useUpdateProject();
  const queryClient = useQueryClient();
  
  // Fetch all tasks to get the ones for this project
  const { data: allTasks } = useTasks(true); // Get all tasks
  const { data: users } = useUsers();
  const { data: clients } = useClients();
  const updateProjectMutation = useUpdateProject(); // Get all users for name resolution
  
  // Function to get tasks for this specific project
  const getProjectTasks = () => {
    if (!allTasks) return [];
    return allTasks.filter(task => {
      if (!task.project) return false;
      const taskProjectId = typeof task.project === 'string' ? task.project : task.project._id;
      return taskProjectId === project._id;
    });
  };
  
  const projectTasks = getProjectTasks();

  const handleDeleteTask = async (task: { _id: string }) => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      await deleteTask(task._id);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch {
      // error surfaced by mutation if configured
    }
  };
  
  // Function to get user names from IDs
  const getUserNames = (userIds: string[] | undefined) => {
    if (!userIds || userIds.length === 0) return 'Not specified';
    if (!users) return 'Loading...';
    
    return userIds.map((userId: string) => {
      const user = users.find(u => u._id === userId);
      return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
    }).join(', ');
  };

  const showBackButton = location.pathname === `/dashboard/project/${project._id}/details`;

  // Persist last viewed task id
  const setLastViewedTaskId = useCallback((taskId: string | null) => {
    try {
      if (taskId) {
        localStorage.setItem(LAST_VIEWED_TASK_KEY, taskId);
      } else {
        localStorage.removeItem(LAST_VIEWED_TASK_KEY);
      }
    } catch {}
  }, []);

  const handleUpdateProject = async (projectData: CreateProjectData) => {
    if (!project._id || !projectData.projectName.trim()) return;
    
    await updateProjectMutation.mutateAsync({
      id: project._id,
      data: {
        projectName: projectData.projectName,
        type: projectData.type,
        client: projectData.client,
        description: projectData.description,
        authorizedBy: projectData.authorizedBy,
        assignedToUser: projectData.assignedToUser,
        quotedPrice: projectData.quotedPrice,
        agreedPrice: projectData.agreedPrice,
        toBeginOn: projectData.toBeginOn,
        toBeCompletedBy: projectData.toBeCompletedBy,
        spec: projectData.spec,
      }
    });
    
    onProjectModalClose();
  };

  const handleArchiveToggle = async () => {
    if (!project._id) return;
    await archiveProjectMutation.mutateAsync({ id: project._id, archived: !project.archived });
  };

  if (project.archived) {
    return (
      <Box p={6} borderWidth={1} borderRadius="lg" boxShadow="md" bg="gray.100" opacity={0.7} textAlign="center">
        <Heading size="lg" mb={2} color="gray.600">
          {project.projectName}
          <Badge ml={2} colorScheme="gray" variant="solid">Archived</Badge>
        </Heading>
        <Text fontSize="md" color="gray.600" mb={4}>
          This project is archived and cannot be edited or have tasks added.<br />
          You can unarchive it to make changes.
        </Text>
        
        <Divider my={4} />
        <Heading size="md" mb={2}>Tasks</Heading>
        {projectTasks.length > 0 ? (
          <TaskTable
            tasks={projectTasks}
            getProjectName={() => project.projectName}
            getUserNames={getUserNames}
            getPriorityColor={(priority) => {
              switch (priority?.toLowerCase()) {
                case 'urgent': return 'red';
                case 'high': return 'orange';
                case 'medium': return 'yellow';
                case 'low': return 'green';
                default: return 'gray';
              }
            }}
            getStatusColor={(status) => {
              switch (status?.toLowerCase()) {
                case 'not started': return 'gray';
                case 'completed': return 'green';
                case 'in progress': return 'blue';
                case 'pending': return 'yellow';
                case 'on hold': return 'red';
                default: return 'gray';
              }
            }}
            handleViewTask={() => {}}
          handleEditTask={(task) => {
            setTaskFormData({
              taskTitle: task.taskTitle || '',
              priority: task.priority || 'medium',
              description: task.description || '',
              type: task.type || 'development',
              status: task.status || 'In progress',
              authBy: task.authBy || [],
              quotedPrice: task.quotedPrice,
              agreedPrice: task.agreedPrice,
              dateRequested: task.dateRequested,
              dateStarted: task.dateStarted,
              dueDate: task.dueDate,
              assignedToUser: task.assignedToUser || [],
              _id: task._id,
              project: project._id,
            });
            setIsTaskModalOpen(true);
          }}
          handleDeleteTask={handleDeleteTask}
          />
        ) : (
          <Text color="gray.500">No tasks for this project.</Text>
        )}
      </Box>
    );
  }

  if (viewTaskId) {
    return (
      <Box p={6} borderWidth={1} borderRadius="lg" boxShadow="md" bg="white">
        <Flex justifyContent="end" alignItems="center" mb={4}>
          <Button mb={4} onClick={() => {
          setViewTaskId(null);
          setLastViewedTaskId(null);
        }} colorScheme="blue" 
        borderRadius={'25px'} size="md" fontSize="sm" px={4} py={2} ml={2}
        >
          Back to Project Details
        </Button>
          </Flex>
        <Divider orientation="horizontal" mx={4} />
        <TaskDetails taskId={viewTaskId} onClose={() => {
          setViewTaskId(null);
          setLastViewedTaskId(null);
        }} 
        showTopHeader={false} // Hide top header
        />
      </Box>
    );
  }

  return (
    <Box p={8} borderWidth={0} borderRadius="2xl" boxShadow="1xl" bgGradient="linear(to-br, white, gray.50)" opacity={project.archived ? 0.5 : 1}>
      {/* Header Section with Improved Layout */}
      <Box bg="white" p={6} borderRadius="xl" boxShadow="sm" mb={6} border="1px" borderColor="gray.100">
        <Flex justify="space-between" align="flex-start" mb={4} wrap="wrap" gap={4}>
          <Box flex="1">
            <HStack spacing={3} mb={3}>
              <Heading size="xl" color="blue.800" letterSpacing="tight">
                {project.projectName}
              </Heading>
              {project.archived && (
                <Badge ml={2} colorScheme="gray" variant="solid" fontSize="sm" px={3} py={1} borderRadius="full">
                  Archived
                </Badge>
              )}
            </HStack>
            <HStack spacing={4} mb={2}>
              <Badge colorScheme="purple" fontSize="sm" px={3} py={1} borderRadius="full" variant="subtle">
                {project.type}
              </Badge>
              <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full" variant="subtle">
                {projectTasks.length} Tasks
              </Badge>
              <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full" variant="subtle">
                {project.projectNumber}
              </Badge>
            </HStack>
            <Text fontSize="md" color="gray.600" maxW="600px">
              Client: <Text as="span" fontWeight="semibold" color="gray.800">
                {typeof project.client === 'string' ? project.client : project.client?.clientName || 'Unknown'}
              </Text>
            </Text>
          </Box>
          <VStack spacing={3} align="stretch" minW="200px">
            {showBackButton && (
              <Button
                size="md"
                colorScheme="green"
                variant="solid"
                borderRadius="lg"
                px={6}
                fontWeight="semibold"
                shadow="sm"
                _hover={{ shadow: "md", transform: "translateY(-1px)" }}
                transition="all 0.2s"
                onClick={() => navigate('/dashboard/projects')}
                isDisabled={project.archived}
              >
                ← Back to All Projects
              </Button>
            )}
            <Button
              size="md"
              colorScheme="orange"
              variant="solid"
              borderRadius="lg"
              px={6}
              fontWeight="semibold"
              shadow="sm"
              _hover={{ shadow: "md", transform: "translateY(-1px)" }}
              transition="all 0.2s"
              leftIcon={<EditIcon />}
              onClick={onProjectModalOpen}
              isDisabled={project.archived || !canEditOrDeleteOrArchiveOrAddTask}
            >
              Edit Project
            </Button>
            <Button
              size="md"
              colorScheme="blue"
              variant="solid"
              borderRadius="lg"
              px={6}
              fontWeight="semibold"
              shadow="sm"
              _hover={{ shadow: "md", transform: "translateY(-1px)" }}
              transition="all 0.2s"
              onClick={() => {
                // Reset form data for new task
                setTaskFormData({
                  taskTitle: '',
                  priority: 'medium',
                  description: '',
                  type: 'development',
                  status: 'Not Started',
                  authBy: [],
                  quotedPrice: undefined,
                  agreedPrice: undefined,
                  dateRequested: undefined,
                  dateStarted: undefined,
                  dueDate: undefined,
                  assignedToUser: [],
                });
                setIsTaskModalOpen(true);
              }}
              isDisabled={project.archived || !canEditOrDeleteOrArchiveOrAddTask}
            >
              + Add Task
            </Button>
          </VStack>
        </Flex>
      </Box>
      {/* Task Modal for adding a new task */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          // Reset form data when modal is closed
          setTaskFormData({
            taskTitle: '',
            priority: 'medium',
            description: '',
            type: 'development',
            status: 'Not Started',
            authBy: [],
            quotedPrice: undefined,
            agreedPrice: undefined,
            dateRequested: undefined,
            dateStarted: undefined,
            dueDate: undefined,
            assignedToUser: [],
          });
        }}
        formData={taskFormData}
        onFormChange={data => setTaskFormData({ ...taskFormData, ...data })}
        onSubmit={async () => {
          try {
            if (taskFormData._id) {
              // Update existing task
              await updateTask({
                id: taskFormData._id,
                data: {
                  taskTitle: taskFormData.taskTitle,
                  priority: taskFormData.priority,
                  description: taskFormData.description,
                  type: taskFormData.type,
                  status: taskFormData.status,
                  authBy: taskFormData.authBy,
                  quotedPrice: taskFormData.quotedPrice,
                  agreedPrice: taskFormData.agreedPrice,
                  dateRequested: taskFormData.dateRequested,
                  dateStarted: taskFormData.dateStarted,
                  dueDate: taskFormData.dueDate,
                  assignedToUser: taskFormData.assignedToUser,
                  project: project._id,
                }
              });
            } else {
              // Create new task
              await createTask({ ...taskFormData, project: project._id });
            }
            // Refetch project data to get updated tasks
            await queryClient.invalidateQueries({ queryKey: ['projects'] });
            await queryClient.invalidateQueries(); // fallback: refetch everything if needed
            setIsTaskModalOpen(false);
            // Reset form data
            setTaskFormData({
              taskTitle: '',
              priority: 'medium',
              description: '',
              type: 'development',
              status: 'Not Started',
              authBy: [],
              quotedPrice: undefined,
              agreedPrice: undefined,
              dateRequested: undefined,
              dateStarted: undefined,
              dueDate: undefined,
              assignedToUser: [],
            });
          } catch (e) {
            // Optionally show error toast
          }
        }}
        isLoading={taskFormData._id ? isUpdatingTask : isCreatingTask}
        selectedProject={project}
        onProjectChange={() => {}}
        title={taskFormData._id ? "Update Task" : "Add Task to Project"}
        hideProjectField={true}
      />
      
      {/* Project Edit Modal */}
      <ProjectModal
        isOpen={isProjectModalOpen}
        onClose={onProjectModalClose}
        onSave={handleUpdateProject}
        isLoading={updateProjectMutation.isPending}
        editingProject={project}
        formData={formData}
        setFormData={setFormData}
        projectTypes={projectTypes}
        clients={clients || []}
      />
      
      <Divider my={6} />

      {/* Project Details Grid with Improved Layout */}
      <Box bg="white" p={6} borderRadius="xl" boxShadow="sm" mb={6} border="1px" borderColor="gray.100">
        <Heading size="md" mb={4} color="gray.700" fontWeight="semibold">
          Project Details
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={4}>
          <VStack align="start" spacing={2} p={4} bg="gray.50" borderRadius="lg">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">Project Number</Text>
            <Text fontSize="lg" color="gray.800" fontWeight="semibold">{project.projectNumber}</Text>
          </VStack>
          <VStack align="start" spacing={2} p={4} bg="gray.50" borderRadius="lg">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">Created</Text>
            <Text fontSize="lg" color="gray.800" fontWeight="semibold">
              {project.createdAt ? new Date(project.createdAt).toLocaleDateString() : 'N/A'}
            </Text>
          </VStack>
          <VStack align="start" spacing={2} p={4} bg="gray.50" borderRadius="lg">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">Created By</Text>
            <Text fontSize="lg" color="gray.800" fontWeight="semibold">
              {project.userWhoCreatedProject?.firstName} {project.userWhoCreatedProject?.lastName}
            </Text>
          </VStack>
          <VStack align="start" spacing={2} p={4} bg="blue.50" borderRadius="lg">
            <Text fontSize="sm" color="blue.600" fontWeight="medium">Assigned To</Text>
            <Text fontSize="lg" color="blue.800" fontWeight="semibold">
              {project.assignedToUser && project.assignedToUser.length > 0 
                ? project.assignedToUser.map((u: any) => u.firstName ? `${u.firstName} ${u.lastName}` : u).join(', ') 
                : 'Not specified'}
            </Text>
          </VStack>
          <VStack align="start" spacing={2} p={4} bg="purple.50" borderRadius="lg">
            <Text fontSize="sm" color="purple.600" fontWeight="medium">Authorized By</Text>
            <Text fontSize="lg" color="purple.800" fontWeight="semibold">
              {project.authorizedBy && project.authorizedBy.length > 0
                ? project.authorizedBy.map((u: any) => {
                    if (u && typeof u === 'object' && (u.firstName || u.lastName)) {
                      return `${u.firstName || ''} ${u.lastName || ''}`.trim();
                    } else if (typeof u === 'string') {
                      return u;
                    } else {
                      return 'Unknown';
                    }
                  }).join(', ')
                : 'Not specified'}
            </Text>
          </VStack>
        </SimpleGrid>
        
        {/* Financial & Timeline Info */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mt={6}>
          <VStack align="start" spacing={2} p={4} bg="green.50" borderRadius="lg" border="1px" borderColor="green.200">
            <Text fontSize="sm" color="green.600" fontWeight="medium">Quoted Price</Text>
            <Text fontSize="xl" color="green.700" fontWeight="bold">
              {project.quotedPrice ? `£${project.quotedPrice.toLocaleString()}` : '—'}
            </Text>
          </VStack>
          <VStack align="start" spacing={2} p={4} bg="green.50" borderRadius="lg" border="1px" borderColor="green.200">
            <Text fontSize="sm" color="green.600" fontWeight="medium">Agreed Price</Text>
            <Text fontSize="xl" color="green.700" fontWeight="bold">
              {project.agreedPrice ? `£${project.agreedPrice.toLocaleString()}` : '—'}
            </Text>
          </VStack>
          <VStack align="start" spacing={2} p={4} bg="orange.50" borderRadius="lg" border="1px" borderColor="orange.200">
            <Text fontSize="sm" color="orange.600" fontWeight="medium">Start Date</Text>
            <Text fontSize="lg" color="orange.700" fontWeight="semibold">
              {project.toBeginOn ? new Date(project.toBeginOn).toLocaleDateString() : '—'}
            </Text>
          </VStack>
          <VStack align="start" spacing={2} p={4} bg="red.50" borderRadius="lg" border="1px" borderColor="red.200">
            <Text fontSize="sm" color="red.600" fontWeight="medium">Due Date</Text>
            <Text fontSize="lg" color="red.700" fontWeight="semibold">
              {project.toBeCompletedBy ? new Date(project.toBeCompletedBy).toLocaleDateString() : '—'}
            </Text>
          </VStack>
        </SimpleGrid>
      </Box>

      {/* Project Description Section */}
      <Box bg="white" p={6} borderRadius="xl" boxShadow="sm" mb={6} border="1px" borderColor="gray.100">
        <Heading size="md" mb={4} color="gray.700" fontWeight="semibold">
          Project Description
        </Heading>
        <Box 
          bg="#f6fcff" 
          p={6} 
          borderRadius="lg" 
          borderLeft="4px" 
          borderLeftColor="blue.400"
          maxH="600px" 
          overflowY="auto"
          _hover={{ opacity: 0.8 }}
          transition="background 0.2s"
        >
          {project.description ? (
            <Box 
              fontSize="md" 
              color="gray.800" 
              p={4} 
              lineHeight="1.6" 
              dangerouslySetInnerHTML={{ __html: project.description }}
              sx={{
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  marginBottom: '10px',
                  fontWeight: 'bold',
                },
                '& hr': {
                  border: 'none',
                  borderTop: '1px solid #e2e8f0',
                  margin: '20px 0',
                },
                '& ul': {
                  marginLeft: '40px',
                  paddingLeft: '0',
                  listStyleType: 'disc',
                  marginBottom: '20px',
                },
                '& ol': {
                  marginLeft: '20px',
                  paddingLeft: '0',
                  listStyleType: 'decimal',
                  marginBottom: '16px',
                },
                '& li': {
                  marginBottom: '8px',
                  paddingLeft: '8px', 
                  '& p': {
                    marginBottom: '10px',
                  },
                },
                '& a': {
                  color: '#3097e3',
                  textDecoration: 'underline',
                  '&:hover': {
                    color: '#2082d1',
                  },
                },
                '& p': {
                  marginBottom: '15px',
                },
              }}
            />
          ) : (
            <Text fontSize="md" color="gray.500" fontStyle="italic">No description provided</Text>
          )}
        </Box>
      </Box>
      {/* Project Specifications Section */}
      {project.spec && Array.isArray(project.spec) && project.spec.length > 0 && (
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm" mb={6} border="1px" borderColor="gray.100">
          <Heading size="md" mb={4} color="gray.700" fontWeight="semibold">
            Project Specifications
          </Heading>
          <VStack align="stretch" spacing={6}>
            {project.spec.map((spec: any, idx: number) => {
              let filesArr: { url?: string; fileDescription?: string }[] = Array.isArray(spec.files) ? spec.files : [];
              return (
                <Box key={idx} p={6} borderWidth={1} borderRadius="xl" bg="gray.50" w="100%" border="1px" borderColor="gray.200">
                  <Box mb={6} borderRadius="lg" bg="white" p={6} border="1px" borderColor="gray.200">
                    <div style={{ minHeight: '100px', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: spec.specDescriptionText || '' }} />
                  </Box>
                  <Divider mb={6} />
                  <VStack align="stretch" spacing={4} w="100%">
                    <HStack justify="space-between" align="center">
                      <Heading size="sm" color="gray.600">Specification Files</Heading>
                      <Button
                        size="md"
                        colorScheme="green"
                        borderRadius="lg"
                        fontWeight="semibold"
                        shadow="sm"
                        _hover={{ shadow: "md", transform: "translateY(-1px)" }}
                        transition="all 0.2s"
                        onClick={() => {
                          if (!(window as any).cloudinary) return;
                          (window as any).cloudinary.openUploadWidget(
                            {
                              cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dwnbctmwk',
                              uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'all_files_uploads',
                              sources: ['local', 'url', 'camera'],
                              multiple: false,
                              resourceType: 'auto',
                              clientAllowedFormats: ['pdf', 'doc', 'docx', 'txt', 'jpg', 'png'],
                            },
                            (error: any, result: any) => {
                              if (!error && result.event === 'success') {
                                setPendingSpecIdx(idx);
                                setPendingFileUrl(result.info.secure_url);
                                setPendingFileDesc('');
                                onOpen();
                              }
                            }
                          );
                        }}
                        isLoading={addSpecFileMutation.isPending}
                      >
                        + Add File
                      </Button>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4} w="100%" maxH="450px" overflowY="auto">
                      {Array.isArray(filesArr) && filesArr.length > 0 ? (
                        filesArr.map((fileObj: { url?: string; fileDescription?: string; filename?: string }, fileIdx: number) => {
                          const url = fileObj.url || '';
                          const ext = url.split('.').pop()?.toLowerCase().split('?')[0] || '';
                          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
                          const isPdf = ext === 'pdf';
                          const isDoc = ['doc', 'docx'].includes(ext);
                          // Remove file handler
                          const handleRemoveFile = () => {
                            if (!project._id) return;
                            removeSpecFileMutation.mutate({ projectId: project._id, specIdx: idx, fileIdx });
                          };
                          return (
                            <Box
                              key={fileIdx}
                              borderWidth={1}
                              borderRadius="xl"
                              bg="white"
                              boxShadow="md"
                              overflow="hidden"
                              _hover={{ boxShadow: 'xl', borderColor: 'blue.300', transform: 'translateY(-2px)' }}
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              p={4}
                              transition="all 0.2s"
                            >
                              <HStack spacing={2} mb={3} w="100%" justify="center">
                                {isImage && <Badge colorScheme="teal" borderRadius="full">Image</Badge>}
                                {isPdf && <Badge colorScheme="red" borderRadius="full">PDF</Badge>}
                                {isDoc && <Badge colorScheme="purple" borderRadius="full">DOC</Badge>}
                                {!isImage && !isPdf && !isDoc && <Badge colorScheme="gray" borderRadius="full">{ext.toUpperCase()}</Badge>}
                              </HStack>
                              <Box w="100%" h="120px" display="flex" alignItems="center" justifyContent="center" mb={3} borderRadius="lg" bg="gray.50">
                                {isImage ? (
                                  <img
                                    src={url}
                                    alt="spec preview"
                                    style={{ borderRadius: 8, maxHeight: 100, maxWidth: '100%', cursor: 'pointer' }}
                                    onClick={() => setPreviewImage(url)}
                                  />
                                ) : isPdf ? (
                                  <iframe src={url} title="PDF Preview" width="100%" height="100%" style={{ border: 'none', borderRadius: 8, minHeight: 80 }} />
                                ) : isDoc ? (
                                  <Text fontSize="sm" color="gray.600">DOC/DOCX file (no preview)</Text>
                                ) : (
                                  <Text fontSize="sm" color="gray.400">No preview</Text>
                                )}
                              </Box>
                              <Text fontSize="xs" color="gray.600" mb={3} w="100%" textAlign="center" noOfLines={2}>
                                {fileObj.fileDescription || <span style={{ color: '#bbb' }}>No description</span>}
                              </Text>
                              <VStack w="100%" spacing={2}>
                                <Button as="a" href={url} download target="_blank" size="sm" colorScheme="blue" w="100%" borderRadius="lg">
                                  Download
                                </Button>
                                <Button size="sm" colorScheme="red" variant="outline" onClick={handleRemoveFile} w="100%" borderRadius="lg">
                                  Remove
                                </Button>
                              </VStack>
                            </Box>
                          );
                        })
                      ) : (
                        <Box p={8} textAlign="center" gridColumn="1 / -1">
                          <Text color="gray.500" fontSize="md" fontStyle="italic">No files attached to this specification</Text>
                        </Box>
                      )}
                    </SimpleGrid>
                  </VStack>
                  
                  {/* File Description Modal */}
                  <Modal isOpen={isOpen} onClose={onClose} isCentered>
                    <ModalOverlay />
                    <ModalContent borderRadius="xl">
                      <ModalHeader>Add File Description</ModalHeader>
                      <ModalCloseButton />
                      <ModalBody>
                        <Text mb={3}>You can add an optional description for this file:</Text>
                        <Input
                          placeholder="File description (optional)"
                          value={pendingFileDesc}
                          onChange={e => setPendingFileDesc(e.target.value)}
                          autoFocus
                          borderRadius="lg"
                        />
                      </ModalBody>
                      <ModalFooter>
                        <Button onClick={onClose} mr={3} variant="ghost" borderRadius="lg">Cancel</Button>
                        <Button
                          colorScheme="blue"
                          isLoading={addSpecFileMutation.isPending}
                          borderRadius="lg"
                          onClick={() => {
                            if (pendingSpecIdx !== null && pendingFileUrl) {
                              addSpecFileMutation.mutate({
                                projectId: project._id,
                                specIdx: pendingSpecIdx,
                                url: pendingFileUrl,
                                fileDescription: pendingFileDesc,
                              });
                              onClose();
                              setPendingSpecIdx(null);
                              setPendingFileUrl(null);
                              setPendingFileDesc('');
                            }
                          }}
                        >
                          Add File
                        </Button>
                      </ModalFooter>
                    </ModalContent>
                  </Modal>
                </Box>
              );
            })}
          </VStack>
          
          {/* Image Preview Modal */}
          {previewImage && (
            <Box position="fixed" top={0} left={0} w="100vw" h="100vh" bg="rgba(0,0,0,0.8)" zIndex={2000} display="flex" alignItems="center" justifyContent="center">
              <Box position="relative" bg="white" p={4} borderRadius="xl" boxShadow="2xl" maxW="90vw" maxH="90vh">
                <img src={previewImage} alt="Preview" style={{ maxWidth: '80vw', maxHeight: '80vh', borderRadius: 12 }} />
                <Button 
                  position="absolute" 
                  top={4} 
                  right={4} 
                  size="sm" 
                  colorScheme="red" 
                  borderRadius="lg"
                  onClick={() => setPreviewImage(null)}
                >
                  Close
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Tasks Section */}
      <Box bg="white" p={6} borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
        <Heading size="md" mb={4} color="blue.700" fontWeight="semibold">
          Project Tasks
        </Heading>
        {projectTasks.length > 0 ? (
          <TaskTable
            tasks={projectTasks}
            getProjectName={() => project.projectName}
            getUserNames={getUserNames}
            getPriorityColor={(priority) => {
              switch (priority?.toLowerCase()) {
                case 'urgent': return 'red';
                case 'high': return 'orange';
                case 'medium': return 'yellow';
                case 'low': return 'green';
                default: return 'gray';
              }
            }}
            getStatusColor={(status) => {
              switch (status?.toLowerCase()) {
                case 'not started': return 'gray';
                case 'completed': return 'green';
                case 'in progress': return 'blue';
                case 'pending': return 'yellow';
                case 'on hold': return 'red';
                default: return 'gray';
              }
            }}
            handleViewTask={(task) => {
              setViewTaskId(task._id);
              setLastViewedTaskId(task._id);
              setLastViewedProjectId(project._id);
            }}
            handleEditTask={(task) => {
              setTaskFormData({
                ...task,
                // fallback for missing fields
                taskTitle: task.taskTitle || '',
                priority: task.priority || 'medium',
                description: task.description || '',
                type: task.type || 'development',
                status: task.status || 'In progress',
                authBy: task.authBy || [],
                quotedPrice: task.quotedPrice,
                agreedPrice: task.agreedPrice,
                dateRequested: task.dateRequested,
                dateStarted: task.dateStarted,
                dueDate: task.dueDate,
                assignedToUser: task.assignedToUser || [],
                _id: task._id,
              });
              setIsTaskModalOpen(true);
            }}
            handleDeleteTask={handleDeleteTask}
          />
        ) : (
          <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
            <Text color="gray.500" fontSize="lg" fontStyle="italic">
              No tasks have been created for this project yet.
            </Text>
            <Text color="gray.400" fontSize="md" mt={2}>
              Click "Add Task" to create your first task.
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProjectOverview;
